// Copyright 2018 Sorama B.V.

import { autoinject, observable, transient } from "aurelia-framework";
import { Quad } from "datatypes/Generic/Quad";
import { GenericMeasurement } from "datatypes/SoramaWebApi";
import { MeasurementExtensions } from "extensions/MeasurementExtensions";
import { URA } from "libraries/Math/URA";
import { IModal } from "libraries/WorkflowFramework/Interfaces/IModal";
import { IWorkflowModuleModalInstance } from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";

@transient()
@autoinject
export class ImageTransformModal implements IModal<ImageTransformModalState> {
    public image: string;
    public deviceAspectRatio: number;
    public deviceRegion: Quad = {
        topLeft: { x: -0.08, y: 0.08 },
        topRight: { x: 0.08, y: 0.08 },
        bottomLeft: { x: -0.08, y: -0.08 },
        bottomRight: { x: 0.08, y: -0.08 },
    };

    @observable
    public regionOfInterest: Quad;
    private originalRegionOfInterest: Quad;
    private state: ImageTransformModalState;

    public activateModal(
        state: ImageTransformModalState,
        modalInstance: IWorkflowModuleModalInstance<ImageTransformModalState>
    ): void {
        this.state = state;

        // Get microphone positions for measurement
        const microphonePositions = MeasurementExtensions.getMicrophonePositionsForMeasurement(state.measurement);

        // Get URA properties
        const uraProperties = URA.getUraProperties3D(microphonePositions);

        if (uraProperties.isUra) {
            // Device is an ura, easy to get an aspect ratio
            this.deviceAspectRatio = uraProperties.width / uraProperties.height;
        } else {
            // Device is not an ura.. unclear what the region should be, use 1 for now
            this.deviceAspectRatio = 1;
        }

        this.image = state.image;
        this.regionOfInterest = state.regionOfInterest ? state.regionOfInterest : Quad.unitQuad;
        this.originalRegionOfInterest = Quad.clone(this.regionOfInterest);
    }

    public deactivate(): ImageTransformModalState {
        this.state.regionOfInterest = this.regionOfInterest;
        return this.state;
    }

    public regionOfInterestChanged(newRegion: Quad): void {
        if (this.state.onRegionOfInterestChanged) {
            this.state.onRegionOfInterestChanged(newRegion);
        }
    }

    //#region Button callbacks

    public resetRegion(): void {
        this.regionOfInterest = Quad.clone(this.originalRegionOfInterest);
    }

    //#endregion
}

export interface ImageTransformModalState {
    image: string;
    measurement: GenericMeasurement;

    regionOfInterest?: Quad;
    onRegionOfInterestChanged?: (regionOfInterest: Quad) => void;
}
